import React, {
  useCallback, useContext, useEffect, useMemo, useRef, useState,
} from 'react';
import ModalMaterial from '@mui/material/Modal';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import { AppContext } from 'Context';
import { axios, dataFetcher, endpoints } from 'Api';
import {
  Users, Video, Image as ImageIcon, Check, X, Plus, Trash2, UserPlus, Search,
  Sparkles, Layers, ChevronRight,
} from 'Utils/lucideIcons';
import PersonButton from 'Components/share-screen/components/PersonButton';
import { SquareCheckbox } from 'Pages/private/videos/components/CustomDesigns';
import styles from './groupWizard.module.css';

const EMAIL_REGEX = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i;

const PLACEHOLDER_THUMB = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjE1MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMjAwIiBoZWlnaHQ9IjE1MCIgZmlsbD0iI2VlZjBmMyIvPjwvc3ZnPg==';

const MODAL_BOX_SX = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '94%',
  maxWidth: '1080px',
  height: '88vh',
  bgcolor: '#ffffff',
  borderRadius: '16px',
  boxShadow: '0 24px 70px rgba(15, 23, 42, 0.22)',
  outline: 'none',
  overflow: 'hidden',
};

// One-tap starters for a brand-new group; the field stays freely editable.
const NAME_SUGGESTIONS = ['Family', 'Close Friends', 'Grandkids', 'Work', 'Everyone'];

// Matches the /content-layout listing: 24 per scroll-load, 48 on first paint.
const CONTENT_PAGE_SIZE = 24;
const CATEGORY_LIMIT = 100;

const STEPS = [
  { label: 'Group info', hint: 'Name the group' },
  { label: 'Members', hint: 'Who is in the group' },
  { label: 'Content', hint: 'What they can watch' },
];

// A member is "registered" when the backend returned a real name for them —
// those go to the API as ids, everyone else goes as an email invite. Same rule
// the header's create-group flow uses, kept identical so payloads match.
const isRegistered = (person) => Boolean(
  person?.firstname && `${person.firstname}`.trim() !== ''
  && person?.lastname && `${person.lastname}`.trim() !== ''
);

const displayName = (person) => {
  const name = `${person?.firstname || ''} ${person?.lastname || ''}`.trim();
  return name || person?.email || 'Unknown';
};

const cleanUrl = (value) => (typeof value === 'string' && value ? value.replace(/\\/g, '') : null);

// Thumbnail field varies per endpoint: getCategoryDetail videos expose
// imagekit_icon / thumbnails[] / thumbnail, getMyAlbum albums expose
// imagekit_icon / icon. Same precedence /content-layout uses.
const resolveThumb = (item, isVideo) => {
  const candidates = isVideo
    ? [item?.imagekit_icon, item?.thumbnails?.[0], item?.thumbnail, item?.thumbnails]
    : [item?.imagekit_icon, item?.icon, item?.image, item?.thumbnail];
  const hit = candidates.find((value) => typeof value === 'string' && value);
  return cleanUrl(hit) || PLACEHOLDER_THUMB;
};

// moveFilesToGroup and getVideosAndALbumsOfGroup both speak in *content* ids,
// so prefer `content_id` when the listing carries one — the page's previous
// category-filtered picker did the same. Albums only ever expose `id`.
const contentItemId = (item) => String(item?.content_id || item?.id || item?.video_id);

// Date field also varies per endpoint; take whichever the item carries.
const itemDate = (item) => {
  const raw = item?.created_at || item?.published_at || item?.publish_date
    || item?.created || item?.uploaded_at;
  return typeof raw === 'string' && raw ? raw.split(' ')[0] : null;
};

const sameIds = (a, b) => a.length === b.length
  && [...a].sort().join(',') === [...b].sort().join(',');

/**
 * The one modal used for both "New Group" and editing an existing group.
 *
 * Three steps — info, members, content — freely navigable in both modes. Nothing
 * is written until Save, which then applies name, member and content changes in
 * that order against the same endpoints the page used before.
 */
const GroupWizardModal = ({ open, mode, group, onClose, onSaved }) => {
  const { userDetails, setAppSnackbar } = useContext(AppContext);
  const isEdit = mode === 'edit';
  const groupId = group?.id;

  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [showErrors, setShowErrors] = useState(false);

  /* ---------------- step 1: group info ---------------- */
  const [name, setName] = useState('');

  /* ---------------- step 2: members ---------------- */
  const [loadingMembers, setLoadingMembers] = useState(false);
  const [existingMembers, setExistingMembers] = useState([]);
  const [existingPending, setExistingPending] = useState([]);
  const [removedMemberIds, setRemovedMemberIds] = useState([]);
  const [removedPendingEmails, setRemovedPendingEmails] = useState([]);
  const [newUsers, setNewUsers] = useState([]);
  const [viewers, setViewers] = useState([]);
  const [emailToSearch, setEmailToSearch] = useState('');
  const [userList, setUserList] = useState([]);
  const [searchingUsers, setSearchingUsers] = useState(false);
  const [duplicateErrorType, setDuplicateErrorType] = useState(null);
  const [isSelfSelected, setIsSelfSelected] = useState(false);

  /* ---------------- step 3: content ---------------- */
  const [loadingGroupContent, setLoadingGroupContent] = useState(false);
  // id -> item descriptor, kept per type so the "selected" tray can render
  // titles/thumbnails for items that came from the group as well as ones the
  // user just picked out of the browser.
  const [selectedVideos, setSelectedVideos] = useState({});
  const [selectedAlbums, setSelectedAlbums] = useState({});
  const [initialVideoIds, setInitialVideoIds] = useState([]);
  const [initialAlbumIds, setInitialAlbumIds] = useState([]);
  const [contentTab, setContentTab] = useState(0); // 0 videos, 1 albums
  const [categories, setCategories] = useState([]);
  const [activeCategory, setActiveCategory] = useState(null);
  const [items, setItems] = useState([]);
  const [hasMore, setHasMore] = useState(false);
  const [loadingItems, setLoadingItems] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const scrollRef = useRef(null);
  const requestIdRef = useRef(0);
  // Mirrors of the paging state, read inside loadContent so the callback stays
  // stable (a scroll handler rebuilt on every state change loses events).
  const itemsRef = useRef([]);
  const hasMoreRef = useRef(false);
  const loadingMoreRef = useRef(false);

  /* ---------------- open / reset ---------------- */

  useEffect(() => {
    if (!open) return;
    setStep(0);
    setSaving(false);
    setShowErrors(false);
    setName(isEdit ? group?.title || '' : '');
    setExistingMembers([]);
    setExistingPending([]);
    setRemovedMemberIds([]);
    setRemovedPendingEmails([]);
    setNewUsers([]);
    setEmailToSearch('');
    setUserList([]);
    setDuplicateErrorType(null);
    setIsSelfSelected(false);
    setSelectedVideos({});
    setSelectedAlbums({});
    setInitialVideoIds([]);
    setInitialAlbumIds([]);
    setContentTab(0);
    setActiveCategory(null);
    setItems([]);
    itemsRef.current = [];
    setHasMore(false);
    hasMoreRef.current = false;
    loadingMoreRef.current = false;
  }, [open, isEdit, group?.title, group?.id]);

  // Existing group: pull its members and its shared content so both steps open
  // pre-filled.
  useEffect(() => {
    if (!open || !isEdit || !groupId) return undefined;
    let alive = true;

    setLoadingMembers(true);
    dataFetcher(endpoints.getMyGroupMembers, { id: groupId })
      .then((res) => {
        if (!alive) return;
        if (res?.success) {
          setExistingMembers(res?.data?.members || []);
          setExistingPending(res?.pending_members || []);
        }
      })
      .catch(() => { })
      .finally(() => { if (alive) setLoadingMembers(false); });

    setLoadingGroupContent(true);
    dataFetcher(endpoints.getVideosAndALbumsOfGroup, { group_id: groupId })
      .then((res) => {
        if (!alive) return;
        if (res?.success) {
          const videoMap = {};
          (res.video || []).forEach((item) => {
            videoMap[item.id] = {
              id: item.id,
              title: item.title,
              thumbnail: cleanUrl(item.thumbnails) || PLACEHOLDER_THUMB,
              meta: item.Category || '',
            };
          });
          const albumMap = {};
          (res.album || []).forEach((item) => {
            albumMap[item.id] = {
              id: item.id,
              title: item.title,
              thumbnail: cleanUrl(item.icon) || PLACEHOLDER_THUMB,
              meta: item.photo_count ? `${item.photo_count} photos` : '',
            };
          });
          setSelectedVideos(videoMap);
          setSelectedAlbums(albumMap);
          setInitialVideoIds(Object.keys(videoMap));
          setInitialAlbumIds(Object.keys(albumMap));
        }
      })
      .catch(() => { })
      .finally(() => { if (alive) setLoadingGroupContent(false); });

    return () => { alive = false; };
  }, [open, isEdit, groupId]);

  // The projector's viewers double as a quick-pick list on the members step.
  useEffect(() => {
    if (!open) return undefined;
    let alive = true;
    dataFetcher(endpoints.getViewersList, {})
      .then((res) => {
        if (alive) setViewers(res?.success && res?.data ? res.data : []);
      })
      .catch(() => { if (alive) setViewers([]); });
    return () => { alive = false; };
  }, [open]);

  // Categories drive the content step's left rail. Same call /content-layout
  // makes for its sidebar — `thumbnails: false` keeps the payload light.
  useEffect(() => {
    if (!open) return undefined;
    let alive = true;
    dataFetcher(endpoints.getMyCategory, {
      limit: CATEGORY_LIMIT,
      start: 0,
      thumbnails: false,
    })
      .then((res) => {
        if (!alive || !res?.success || !Array.isArray(res.data)) return;
        const mapped = res.data.map((item) => ({
          name: item.title,
          value: item.id,
          count: item.video_count ?? item.videos_count ?? item.count,
        }));
        const allVideos = mapped.find((item) => item.name?.toLowerCase() === 'all videos');
        const rest = mapped.filter((item) => item.name?.toLowerCase() !== 'all videos');
        const ordered = allVideos ? [allVideos, ...rest] : mapped;
        setCategories(ordered);
        setActiveCategory((prev) => prev || ordered[0] || null);
      })
      .catch(() => { });
    return () => { alive = false; };
  }, [open]);

  /* ---------------- member search ---------------- */

  useEffect(() => {
    if (!open || !emailToSearch) {
      setUserList([]);
      return undefined;
    }
    const timer = setTimeout(() => {
      dataFetcher(endpoints.searchUserByEmailFromFrendList, { email: emailToSearch })
        .then(({ success, data }) => {
          const pickedIds = newUsers.map((user) => user.id);
          const matches = (success ? data || [] : []).filter((item) => !pickedIds.includes(item.id));
          const usable = matches.filter(
            (item) => item.status === null || item.status === undefined
          );
          if (usable.length > 0) {
            setUserList(usable.slice(0, 5));
          } else if (matches.length > 0) {
            // Found, but their status blocks adding them (already a viewer, invite
            // pending, …) — say so rather than silently showing nothing.
            setAppSnackbar({
              isVisible: true,
              type: 'error',
              message: 'This user cannot be added right now',
            });
            setUserList([]);
          } else if (EMAIL_REGEX.test(emailToSearch)) {
            // Not a Projector user yet — offer the typed address as an invite.
            setUserList([{
              email: emailToSearch,
              id: emailToSearch,
              status: null,
              firstname: '',
              lastname: '',
            }]);
          } else {
            setUserList([]);
          }
        })
        .catch(() => setUserList([]))
        .finally(() => setSearchingUsers(false));
    }, 500);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [emailToSearch, open]);

  const activeMembers = useMemo(
    () => existingMembers.filter((member) => !removedMemberIds.includes(member.id)),
    [existingMembers, removedMemberIds]
  );
  const activePending = useMemo(
    () => existingPending.filter((member) => !removedPendingEmails.includes(member.email)),
    [existingPending, removedPendingEmails]
  );

  const revalidateSelection = useCallback((nextUsers) => {
    const selfEmail = userDetails?.email?.toLowerCase();
    const self = nextUsers.some((user) => user.email?.toLowerCase() === selfEmail);
    setIsSelfSelected(self);
    if (self) {
      setDuplicateErrorType(null);
      return;
    }
    const inMembers = nextUsers.some((user) => activeMembers.some(
      (member) => member.email === user.email || member.id === user.id
    ));
    const inPending = nextUsers.some((user) => activePending.some(
      (member) => member.email === user.email || member.id === user.id
    ));
    if (inMembers) setDuplicateErrorType('member');
    else if (inPending) setDuplicateErrorType('pending');
    else setDuplicateErrorType(null);
  }, [userDetails?.email, activeMembers, activePending]);

  const selectUserFromList = useCallback((user) => {
    const already = newUsers.some(
      (existing) => existing.id === user.id || existing.email === user.email
    );
    const next = already ? newUsers : [...newUsers, user];
    setNewUsers(next);
    revalidateSelection(next);
    setUserList([]);
    setEmailToSearch('');
  }, [newUsers, revalidateSelection]);

  const removeUserFromList = useCallback((user) => {
    const next = newUsers.filter((existing) => existing.id !== user.id);
    setNewUsers(next);
    revalidateSelection(next);
  }, [newUsers, revalidateSelection]);

  const searchUsers = useCallback(({ target: { value } }) => {
    setEmailToSearch(value || '');
    // Flip to "searching" on the keystroke, not when the debounced request
    // fires, so the dropdown never flashes "no matching people" mid-typing.
    setSearchingUsers(Boolean(value));
  }, []);

  /* ---------------- content browsing ---------------- */

  // Offset pagination against the same endpoints /content-layout uses:
  // getCategoryDetail for a category's videos, getMyAlbum for the album list.
  // Both take limit/start and return only the slice asked for, unlike the old
  // getMyContentsBasedOnUploadDate call that shipped videos *and* albums in one
  // page-numbered payload. First load pulls two pages so the grid is populated
  // straight away; scroll-loads then page in one at a time.
  const loadContent = useCallback(async (loadMore = false) => {
    if (loadMore && (loadingMoreRef.current || !hasMoreRef.current)) return;
    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;

    if (loadMore) {
      loadingMoreRef.current = true;
      setLoadingMore(true);
    } else {
      loadingMoreRef.current = false;
      setLoadingItems(true);
      setItems([]);
      itemsRef.current = [];
      hasMoreRef.current = true;
      setHasMore(true);
    }

    const start = loadMore ? itemsRef.current.length : 0;
    const limit = loadMore ? CONTENT_PAGE_SIZE : CONTENT_PAGE_SIZE * 2;

    try {
      let fetched = [];
      let total = null;
      if (contentTab === 0) {
        const res = await dataFetcher(endpoints.getCategoryDetail, {
          user_id: userDetails?.myId,
          category_id: activeCategory?.value,
          vpm: false,
          limit,
          start,
        });
        if (res?.success) {
          fetched = res.videos || [];
          total = res.video_count ?? null;
        }
      } else {
        const res = await dataFetcher(endpoints.getMyAlbum, {
          limit,
          start,
          thumbnails: false,
          vpm: true,
        });
        if (res?.success && Array.isArray(res.data)) fetched = res.data;
      }

      if (requestIdRef.current !== requestId) return; // a newer request won
      const merged = loadMore ? [...itemsRef.current, ...fetched] : fetched;
      itemsRef.current = merged;
      setItems(merged);
      // getMyAlbum reports no total, so a short page marks the end of the list.
      const more = total != null ? merged.length < total : fetched.length === limit;
      hasMoreRef.current = more;
      setHasMore(more);
    } catch (error) {
      if (requestIdRef.current !== requestId) return;
      if (!loadMore) {
        itemsRef.current = [];
        setItems([]);
      }
      hasMoreRef.current = false;
      setHasMore(false);
    } finally {
      if (requestIdRef.current === requestId) {
        setLoadingItems(false);
        setLoadingMore(false);
      }
      loadingMoreRef.current = false;
    }
  }, [contentTab, activeCategory?.value, userDetails?.myId]);

  // Load (and reload on tab/category change) only once the content step has
  // actually been opened — creating a group by name alone shouldn't page the
  // whole library in. On the videos tab we wait for the category rail, since
  // getCategoryDetail needs a category_id.
  const awaitingCategory = contentTab === 0 && !activeCategory;
  useEffect(() => {
    if (!open || step !== 2 || awaitingCategory) return;
    loadContent(false);
  }, [open, step, awaitingCategory, loadContent]);

  useEffect(() => {
    const container = scrollRef.current;
    if (!container || step !== 2) return undefined;
    const onScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = container;
      if (scrollHeight - scrollTop - clientHeight < 120) loadContent(true);
    };
    container.addEventListener('scroll', onScroll);
    return () => container.removeEventListener('scroll', onScroll);
  }, [step, loadContent]);

  const toggleContent = useCallback((type, descriptor) => {
    const setter = type === 'video' ? setSelectedVideos : setSelectedAlbums;
    setter((prev) => {
      const next = { ...prev };
      if (next[descriptor.id]) delete next[descriptor.id];
      else next[descriptor.id] = descriptor;
      return next;
    });
  }, []);

  const selectedVideoIds = useMemo(() => Object.keys(selectedVideos), [selectedVideos]);
  const selectedAlbumIds = useMemo(() => Object.keys(selectedAlbums), [selectedAlbums]);
  const selectedTotal = selectedVideoIds.length + selectedAlbumIds.length;

  /* ---------------- validation ---------------- */

  const trimmedName = name.trim();
  const step1Valid = trimmedName.length > 0;
  // Creating a group with nobody in it has no meaning — the old create flow
  // enforced the same thing. Editing may legitimately empty a group out.
  const step2Valid = isEdit ? true : newUsers.length > 0;
  const blockingMemberError = duplicateErrorType !== null || isSelfSelected;
  const canSave = step1Valid && step2Valid && !blockingMemberError;

  const stepState = (index) => {
    if (index === 0) return step1Valid ? 'done' : 'missing';
    if (index === 1) return step2Valid && !blockingMemberError ? 'done' : 'missing';
    return selectedTotal > 0 ? 'done' : 'optional';
  };

  const missingSummary = () => {
    // The name field already carries its own inline error right below it in
    // the new-group flow, so the footer doesn't need to repeat it.
    if (!step1Valid) return isEdit ? 'Step 1 needs a group name.' : null;
    if (!step2Valid) return 'Step 2 needs at least one member.';
    if (isSelfSelected) return 'You can’t add yourself to a group.';
    if (duplicateErrorType === 'member') return 'One of the picked people is already a member.';
    if (duplicateErrorType === 'pending') return 'One of the picked people has already been invited.';
    return null;
  };

  /* ---------------- save ---------------- */

  const handleSave = async () => {
    if (!canSave) {
      setShowErrors(true);
      if (!step1Valid) setStep(0);
      else if (!step2Valid || blockingMemberError) setStep(1);
      return;
    }
    setSaving(true);
    try {
      let targetId = groupId;

      // 1. name — addNewGroup doubles as create (no group_id) and rename.
      if (!isEdit) {
        const { data } = await axios.post(endpoints.addNewGroup, { title: trimmedName });
        if (!data?.success) {
          setAppSnackbar({
            isVisible: true,
            type: 'error',
            message: data?.message || 'This group name is not available',
          });
          setSaving(false);
          return;
        }
        targetId = data.id;
      } else if (trimmedName !== (group?.title || '')) {
        const { data } = await axios.post(endpoints.addNewGroup, {
          group_id: targetId,
          title: trimmedName,
        });
        if (!data?.success) {
          setAppSnackbar({
            isVisible: true,
            type: 'error',
            message: data?.message || 'Could not update the group name',
          });
          setSaving(false);
          return;
        }
      }

      // 2. members — removals first, so the "keep" list sent below is accurate
      //    whether the add endpoint appends or replaces.
      if (removedMemberIds.length > 0) {
        await axios.post(endpoints.removeMemberFromGroup, {
          users: removedMemberIds.join(','),
          group_id: targetId,
        });
      }
      for (let i = 0; i < removedPendingEmails.length; i += 1) {
        // Sequential on purpose: the backend cancels by email, one call each.
        // eslint-disable-next-line no-await-in-loop
        await axios.post(endpoints.cancelInvitation, { email: removedPendingEmails[i] });
      }

      if (newUsers.length > 0) {
        const keep = [...activeMembers, ...activePending];
        const userIds = [...new Set([
          ...keep.filter(isRegistered).map((member) => member.id).filter(Boolean),
          ...newUsers.filter(isRegistered).map((user) => user.id).filter(Boolean),
        ])];
        const emails = [...new Set([
          ...keep.filter((member) => !isRegistered(member)).map((member) => member.email).filter(Boolean),
          ...newUsers.filter((user) => !isRegistered(user)).map((user) => user.email).filter(Boolean),
        ])];
        const { data } = await axios.post(endpoints.addMemberToGroup, {
          group_id: targetId,
          ...(userIds.length > 0 && { users: userIds.join(',') }),
          ...(emails.length > 0 && { emails: emails.join(',') }),
        });
        if (!data?.success) {
          setAppSnackbar({
            isVisible: true,
            type: 'error',
            message: data?.message || 'Could not add members to the group',
          });
        }
      }

      // 3. content — moveFilesToGroup takes the full desired set, so an empty
      //    string is how a group gets cleared out.
      const contentChanged = !isEdit
        ? selectedTotal > 0
        : !sameIds(selectedVideoIds, initialVideoIds) || !sameIds(selectedAlbumIds, initialAlbumIds);
      if (contentChanged) {
        await axios.post(endpoints.moveFilesToGroup, {
          group_id: targetId,
          video_id: selectedVideoIds.join(','),
          album_id: selectedAlbumIds.join(','),
        });
      }

      setAppSnackbar({
        isVisible: true,
        message: isEdit ? 'Group updated' : 'Group created',
      });
      if (onSaved) onSaved();
      onClose();
    } catch (error) {
      setAppSnackbar({
        isVisible: true,
        type: 'error',
        message: 'Oops! Something went wrong',
      });
    } finally {
      setSaving(false);
    }
  };

  /* ---------------- render helpers ---------------- */

  const renderMemberRow = (person, kind) => {
    const removed = kind === 'pending'
      ? removedPendingEmails.includes(person.email)
      : removedMemberIds.includes(person.id);
    return (
      <div
        key={`${kind}-${person.id || person.email}`}
        className={`${styles.memberRow} ${removed ? styles.memberRowRemoved : ''}`}
      >
        <div className={styles.memberAvatar}>
          {person.image
            ? <img src={person.image} alt={displayName(person)} />
            : displayName(person).charAt(0).toUpperCase()}
        </div>
        <div className={styles.memberInfo}>
          <div className={styles.memberName}>{displayName(person)}</div>
          {/* Invitees have no name yet, so their email is already the title —
              don't print it twice. */}
          {displayName(person) !== person.email && (
            <div className={styles.memberEmail}>{person.email}</div>
          )}
        </div>
        {kind === 'pending' && <span className={`${styles.badge} ${styles.badgePending}`}>Pending</span>}
        {removed && <span className={`${styles.badge} ${styles.badgeRemoved}`}>Removing</span>}
        <button
          type="button"
          className={`${styles.rowAction} ${removed ? '' : styles.rowActionDanger}`}
          onClick={() => {
            if (kind === 'pending') {
              setRemovedPendingEmails((prev) => (removed
                ? prev.filter((email) => email !== person.email)
                : [...prev, person.email]));
            } else {
              setRemovedMemberIds((prev) => (removed
                ? prev.filter((id) => id !== person.id)
                : [...prev, person.id]));
            }
          }}
        >
          {removed ? <Plus size={13} /> : <Trash2 size={13} />}
          {removed ? 'Undo' : (kind === 'pending' ? 'Cancel invite' : 'Remove')}
        </button>
      </div>
    );
  };

  // Card layout mirrors the /your-videos grid: thumbnail on top, title +
  // description, meta chips, and a square checkbox overlay. Selected cards get
  // the same dark border + ring treatment that list uses.
  const renderContentCard = (item) => {
    const isVideo = contentTab === 0;
    // getCategoryDetail videos key off `id`; getMyAlbum albums likewise. The
    // fallbacks cover older shapes so the ids still match what
    // getVideosAndALbumsOfGroup returned for the group's existing content.
    const id = contentItemId(item);
    const thumbnail = resolveThumb(item, isVideo);
    const descriptor = {
      id,
      title: item?.title || 'Untitled',
      thumbnail,
      meta: isVideo
        ? (item?.category || item?.Category || item?.category_name || '')
        : (item?.photo_count != null ? `${item.photo_count} photos` : ''),
    };
    const selected = isVideo ? Boolean(selectedVideos[id]) : Boolean(selectedAlbums[id]);
    const toggle = () => toggleContent(isVideo ? 'video' : 'album', descriptor);
    return (
      <div
        role="button"
        tabIndex={0}
        key={`${contentTab}-${id}`}
        className={`${styles.videoCard} ${selected ? styles.videoCardSelected : ''}`}
        onClick={toggle}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            toggle();
          }
        }}
      >
        <div
          className={styles.cardHeader}
          role="presentation"
          onClick={(e) => e.stopPropagation()}
        >
          <SquareCheckbox selected={selected} onClick={toggle} />
        </div>
        <div className={styles.cardContent}>
          <div className={styles.thumbnailContainer}>
            <img
              className={styles.thumbnail}
              src={thumbnail}
              alt={descriptor.title}
              loading="lazy"
              onError={(e) => {
                if (e.target.src !== PLACEHOLDER_THUMB) e.target.src = PLACEHOLDER_THUMB;
              }}
            />
          </div>
          <div className={styles.cardInfo}>
            <h3 className={styles.cardTitle}>{descriptor.title}</h3>
            <p className={styles.cardDescription}>
              {item?.description || item?.video_description || ''}
            </p>
            <div className={styles.cardMeta}>
              {selected && <span className={styles.chipSelected}>Selected</span>}
              {descriptor.meta && (
                <span className={styles.chipCategory} title={descriptor.meta}>
                  {descriptor.meta}
                </span>
              )}
              {itemDate(item) && (
                <span className={styles.chipNeutral}>{itemDate(item)}</span>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Selected items float to the front of the same grid, so what already belongs
  // to the group reads first without splitting the list in two.
  const orderedItems = [...items].sort((a, b) => {
    const key = (item) => {
      const id = contentItemId(item);
      return (contentTab === 0 ? Boolean(selectedVideos[id]) : Boolean(selectedAlbums[id])) ? 0 : 1;
    };
    return key(a) - key(b);
  });

  /* ---------------- steps ---------------- */

  const renderInfoStep = () => {
    const memberTotal = activeMembers.length + activePending.length + newUsers.length;
    return (
      <div className={styles.infoStep}>
        <div className={`${styles.heroIcon} ${!isEdit ? styles.heroIconAdd : ''}`}>
          {group?.image
            ? <img src={group.image} alt="" />
            : (trimmedName
              ? trimmedName.charAt(0).toUpperCase()
              : <Users size={30} strokeWidth={1.8} />)}
        </div>

        <h2 className={styles.infoTitle}>
          {isEdit ? 'Group name' : 'What should we call this group?'}
        </h2>
        <p className={styles.infoSub}>
          {isEdit
            ? 'Renaming a group doesn’t change who is in it or what they can watch.'
            : 'Pick something the people in it will recognise — a family name, a circle of friends, a project.'}
        </p>

        <div className={styles.nameFieldWrap}>
          <input
            id="group-wizard-name"
            className={`${styles.textField} ${styles.textFieldLarge} ${
              showErrors && !step1Valid ? styles.textFieldError : ''
            }`}
            type="text"
            maxLength={70}
            value={name}
            placeholder="Enter a name"
            onChange={(e) => setName(e.target.value)}
          />
          <div className={`${styles.fieldFoot} ${styles.fieldFootCentered}`}>
            <span className={styles.fieldHint}>
              Members see everything shared with this group.
            </span>
            <span className={styles.fieldCounter}>{`${name.length}/70`}</span>
          </div>
          {showErrors && !step1Valid && (
            <div className={styles.errorText}>A group name is required.</div>
          )}
        </div>

        {!isEdit && (
          <div className={styles.suggestBlock}>
            <span className={styles.suggestLabel}>
              <Sparkles size={13} />
              Common names
            </span>
            <div className={styles.suggestChips}>
              {NAME_SUGGESTIONS.map((suggestion) => (
                <button
                  type="button"
                  key={suggestion}
                  className={`${styles.suggestChip} ${
                    trimmedName === suggestion ? styles.suggestChipActive : ''
                  }`}
                  onClick={() => setName(suggestion)}
                >
                  {suggestion}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Live read-out of the other two steps — only meaningful once a
            group already exists to report on, so new groups skip it and
            walk the steps in order instead. */}
        {isEdit && (
          <div className={styles.statStrip}>
            <button
              type="button"
              className={styles.statCard}
              onClick={() => setStep(1)}
            >
              <span className={styles.statIcon}><Users size={17} /></span>
              <span className={styles.statText}>
                <span className={styles.statValue}>
                  {loadingMembers ? '—' : memberTotal}
                </span>
                <span className={styles.statLabel}>
                  {memberTotal === 1 ? 'member' : 'members'}
                </span>
              </span>
              <ChevronRight size={16} className={styles.statArrow} />
            </button>
            <button
              type="button"
              className={styles.statCard}
              onClick={() => setStep(2)}
            >
              <span className={styles.statIcon}><Layers size={17} /></span>
              <span className={styles.statText}>
                <span className={styles.statValue}>
                  {loadingGroupContent ? '—' : selectedTotal}
                </span>
                <span className={styles.statLabel}>
                  {selectedTotal === 1 ? 'item shared' : 'items shared'}
                </span>
              </span>
              <ChevronRight size={16} className={styles.statArrow} />
            </button>
          </div>
        )}
      </div>
    );
  };

  const renderMembersStep = () => {
    const pickedViewers = viewers.filter((viewer) => !newUsers.some(
      (user) => user.id === viewer.id || user.email === viewer.email
    ) && !activeMembers.some(
      (member) => member.id === viewer.id || member.email === viewer.email
    ) && !activePending.some(
      (member) => member.email === viewer.email
    ));

    return (
      <>
        <div className={styles.searchBlock}>
          <div className={styles.sectionHead}>
            <h4 className={styles.sectionTitle}>
              <UserPlus size={15} />
              Add members
              {newUsers.length > 0 && <span className={styles.sectionCount}>{newUsers.length}</span>}
            </h4>
          </div>
          <div className={styles.searchWrap}>
            <div className={styles.searchBar}>
              <Search size={18} className={styles.searchIcon} />
              <input
                className={styles.searchInput}
                type="text"
                value={emailToSearch}
                onChange={searchUsers}
                disabled={saving}
                placeholder="Search by name or email"
              />
              {emailToSearch !== '' && (
                <button
                  type="button"
                  className={styles.searchClear}
                  onClick={() => { setEmailToSearch(''); setUserList([]); }}
                  aria-label="Clear search"
                >
                  <X size={16} />
                </button>
              )}
            </div>
            {emailToSearch !== '' && (
              <div className={styles.searchResults}>
                {searchingUsers ? (
                  <div className={styles.searchStatus}>Searching…</div>
                ) : userList.length > 0 ? (
                  userList.map((user) => (
                    <PersonButton
                      data={user}
                      key={user.id}
                      onClick={() => selectUserFromList(user)}
                    />
                  ))
                ) : (
                  <div className={styles.searchStatus}>No matching people</div>
                )}
              </div>
            )}
          </div>
          {isSelfSelected && (
            <div className={styles.errorText}>You can’t add yourself to a group.</div>
          )}
          {duplicateErrorType === 'member' && (
            <div className={styles.errorText}>This person is already a member of the group.</div>
          )}
          {duplicateErrorType === 'pending' && (
            <div className={styles.errorText}>This person has already been invited.</div>
          )}
        </div>

        <div className={styles.sectionHead}>
          <h4 className={styles.sectionTitle}>
            <Users size={15} />
            {isEdit ? 'Current members' : 'Selected members'}
            <span className={styles.sectionCount}>
              {isEdit
                ? activeMembers.length + activePending.length + newUsers.length
                : newUsers.length}
            </span>
          </h4>
        </div>

        {loadingMembers ? (
          <div className={styles.centered}>
            <CircularProgress size={24} />
            Loading members…
          </div>
        ) : (existingMembers.length + existingPending.length + newUsers.length) === 0 ? (
          <div className={styles.emptyBlock}>
            {isEdit
              ? 'This group has no members yet — search above to add someone.'
              : 'No members picked yet — search above to add at least one person.'}
          </div>
        ) : (
          <div className={styles.memberList}>
            {existingMembers.map((member) => renderMemberRow(member, 'member'))}
            {existingPending.map((member) => renderMemberRow(member, 'pending'))}
            {newUsers.map((user) => (
              <div className={styles.memberRow} key={`new-${user.id}`}>
                <div className={styles.memberAvatar}>
                  {user.image
                    ? <img src={user.image} alt={displayName(user)} />
                    : displayName(user).charAt(0).toUpperCase()}
                </div>
                <div className={styles.memberInfo}>
                  <div className={styles.memberName}>{displayName(user)}</div>
                  {displayName(user) !== user.email && (
                    <div className={styles.memberEmail}>{user.email}</div>
                  )}
                </div>
                <span className={`${styles.badge} ${styles.badgeNew}`}>
                  {isRegistered(user) ? 'Will be added' : 'Will be invited'}
                </span>
                <button
                  type="button"
                  className={`${styles.rowAction} ${styles.rowActionDanger}`}
                  onClick={() => removeUserFromList(user)}
                >
                  <X size={13} />
                  Remove
                </button>
              </div>
            ))}
          </div>
        )}

        {pickedViewers.length > 0 && (
          <div className={styles.viewersBlock}>
            <div className={styles.sectionHead}>
              <h4 className={styles.sectionTitle}>
                <Users size={15} />
                Your viewers
                <span className={styles.sectionCount}>{pickedViewers.length}</span>
              </h4>
            </div>
            <div className={styles.viewersItems}>
              {pickedViewers.map((viewer) => (
                <PersonButton
                  data={viewer}
                  key={viewer.id}
                  onClick={() => selectUserFromList(viewer)}
                />
              ))}
            </div>
          </div>
        )}
      </>
    );
  };

  const renderContentStep = () => (
    <div className={styles.contentLayout}>
      {contentTab === 0 && (
        <div className={styles.sidebar}>
          <div className={styles.sidebarHead}>Categories</div>
          {categories.map((category) => (
            <button
              type="button"
              key={category.value}
              className={`${styles.sidebarItem} ${activeCategory?.value === category.value ? styles.sidebarItemActive : ''}`}
              onClick={() => setActiveCategory(category)}
            >
              <span className={styles.sidebarItemLabel}>{category.name}</span>
              {category.count != null && (
                <span className={styles.sidebarItemCount}>{category.count}</span>
              )}
            </button>
          ))}
        </div>
      )}

      <div className={styles.contentMain}>
        <div className={styles.tabRow}>
          <button
            type="button"
            className={`${styles.tab} ${contentTab === 0 ? styles.tabActive : ''}`}
            onClick={() => setContentTab(0)}
          >
            <Video size={15} />
            Videos
          </button>
          <button
            type="button"
            className={`${styles.tab} ${contentTab === 1 ? styles.tabActive : ''}`}
            onClick={() => setContentTab(1)}
          >
            <ImageIcon size={15} />
            Albums
          </button>
          {selectedTotal > 0 && (
            <span className={styles.selectedPill}>
              <Check size={13} strokeWidth={3} />
              {`${selectedTotal} selected`}
            </span>
          )}
        </div>

        <div className={styles.scrollArea} ref={scrollRef}>
          {loadingItems || loadingGroupContent ? (
            <div className={styles.centered}>
              <CircularProgress size={28} />
              Loading content…
            </div>
          ) : orderedItems.length === 0 ? (
            <div className={styles.emptyBlock}>
              {`No ${contentTab === 0 ? 'videos' : 'albums'} available.`}
            </div>
          ) : (
            <div className={styles.videoGrid}>
              {orderedItems.map(renderContentCard)}
            </div>
          )}

          {loadingMore && (
            <div className={styles.centered}>
              <CircularProgress size={22} />
              Loading more…
            </div>
          )}
        </div>
      </div>
    </div>
  );

  const note = missingSummary();

  return (
    <ModalMaterial open={open} onClose={saving ? undefined : onClose}>
      <Box sx={MODAL_BOX_SX}>
        <div className={styles.wizard}>
          <div className={styles.header}>
            <div className={styles.headerIcon}>
              {group?.image
                ? <img src={group.image} alt="" />
                : (isEdit && (trimmedName || group?.title)
                  ? (trimmedName || group.title).charAt(0).toUpperCase()
                  : <Users size={20} />)}
            </div>
            <div className={styles.headerText}>
              <h3 className={styles.headerTitle}>
                {isEdit ? (trimmedName || group?.title || 'Edit group') : 'New group'}
              </h3>
              <p className={styles.headerSub}>
                {isEdit
                  ? 'Update the name, who belongs to it and what they can watch.'
                  : 'Name the group, pick its members, then choose what to share.'}
              </p>
            </div>
            <button
              type="button"
              className={styles.closeButton}
              onClick={onClose}
              disabled={saving}
              aria-label="Close"
            >
              <X size={17} />
            </button>
          </div>

          <div className={styles.stepper}>
            {isEdit ? (
              STEPS.map((entry, index) => {
                const state = stepState(index);
                const badgeClass = index === step
                  ? ''
                  : state === 'done'
                    ? styles.stepBadgeDone
                    : state === 'missing' && (showErrors || index < step)
                      ? styles.stepBadgeMissing
                      : '';
                return (
                  <button
                    type="button"
                    key={entry.label}
                    className={`${styles.step} ${index === step ? styles.stepActive : ''}`}
                    onClick={() => setStep(index)}
                  >
                    <span className={`${styles.stepBadge} ${badgeClass}`}>
                      {state === 'done' && index !== step
                        ? <Check size={13} strokeWidth={3} />
                        : index + 1}
                    </span>
                    <span className={styles.stepLabels}>
                      <span className={styles.stepLabel}>{entry.label}</span>
                      <span
                        className={`${styles.stepHint} ${
                          state === 'missing' && (showErrors || index < step) ? styles.stepHintMissing : ''
                        }`}
                      >
                        {state === 'missing' && (showErrors || index < step)
                          ? (index === 0 ? 'Name required' : 'At least one member')
                          : entry.hint}
                      </span>
                    </span>
                  </button>
                );
              })
            ) : (
              // New groups walk the steps like a plain progress bar — a
              // circle per step joined by a connector, only the current and
              // already-passed circles are reachable/lit, the rest stay
              // locked until Next reaches them.
              <div className={styles.progressStepper}>
                {STEPS.map((entry, index) => {
                  const isActive = index === step;
                  const isPassed = index < step;
                  const locked = index > step;
                  return (
                    <React.Fragment key={entry.label}>
                      {index > 0 && (
                        <div className={`${styles.progressConnector} ${index <= step ? styles.progressConnectorDone : ''}`} />
                      )}
                      <div className={styles.progressStepItem}>
                        <button
                          type="button"
                          className={`${styles.progressCircle} ${isActive ? styles.progressCircleActive : ''} ${
                            isPassed ? styles.progressCircleDone : ''
                          } ${locked ? styles.progressCircleLocked : ''}`}
                          onClick={() => { if (!locked) setStep(index); }}
                          disabled={locked}
                          aria-disabled={locked}
                        >
                          {isPassed ? <Check size={14} strokeWidth={3} /> : index + 1}
                        </button>
                        <span
                          className={`${styles.progressLabel} ${isActive ? styles.progressLabelActive : ''} ${
                            locked ? styles.progressLabelLocked : ''
                          }`}
                        >
                          {entry.label}
                        </span>
                      </div>
                    </React.Fragment>
                  );
                })}
              </div>
            )}
          </div>

          <div
            className={`${styles.body} ${step === 2 ? styles.bodyFlush : ''} ${
              step === 0 ? styles.bodyCentered : ''
            }`}
          >
            {step === 0 && renderInfoStep()}
            {step === 1 && renderMembersStep()}
            {step === 2 && renderContentStep()}
          </div>

          <div className={styles.footer}>
            {/* New groups keep the footer clean — no status copy, just the
                step name is required. Editing still explains itself here. */}
            <div className={`${styles.footerNote} ${note ? styles.footerNoteWarn : ''}`}>
              {isEdit && (note || (step === 2 && selectedTotal > 0
                ? `${selectedTotal} item${selectedTotal === 1 ? '' : 's'} will be shared with this group.`
                : 'You can move between steps freely — nothing is saved until you hit save.'))}
            </div>
            <button
              type="button"
              className={styles.ghostButton}
              onClick={onClose}
              disabled={saving}
            >
              Cancel
            </button>
            {step > 0 && (
              <button
                type="button"
                className={styles.ghostButton}
                onClick={() => setStep((prev) => prev - 1)}
                disabled={saving}
              >
                Back
              </button>
            )}
            {step < 2 && (
              <button
                type="button"
                className={styles.ghostButton}
                onClick={() => setStep((prev) => prev + 1)}
                disabled={saving}
              >
                Next
              </button>
            )}
            <button
              type="button"
              className={styles.primaryButton}
              onClick={handleSave}
              disabled={saving || !canSave}
              title={note || undefined}
            >
              {saving && <span className={styles.spinner} />}
              {saving
                ? (isEdit ? 'Saving…' : 'Creating…')
                : (isEdit ? 'Save changes' : 'Create group')}
            </button>
          </div>
        </div>
      </Box>
    </ModalMaterial>
  );
};

export default GroupWizardModal;
